import { useEffect, useRef, useState } from 'react';

import { isPrerender } from './default-brand.js';

/**
 * Client-side motion primitives for the marketing home (progressive
 * enhancement). Every hook is a no-op that resolves to the final, static state
 * under three conditions, so nothing here ever hides content or breaks the
 * SEO-critical prerender:
 *
 *   - `prefers-reduced-motion: reduce`  → jump straight to the end state.
 *   - build-time prerender (`isPrerender()`) → end state (real numbers +
 *     revealed content baked into the snapshot).
 *   - missing browser API (IntersectionObserver / rAF) → end state.
 */

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function isStaticContext(): boolean {
  return isPrerender() || prefersReducedMotion();
}

export interface RevealOptions {
  /** Reveal once and stop observing (default true). */
  readonly once?: boolean;
  /** IntersectionObserver rootMargin (default fires slightly before the edge). */
  readonly rootMargin?: string;
}

/**
 * Fade/slide a section in when it scrolls into view. Returns a ref to attach to
 * the element (which must carry the `.reveal` class) and `inView` so callers can
 * drive dependent animations (e.g. count-ups).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options: RevealOptions = {}) {
  const { once = true, rootMargin = '0px 0px -12% 0px' } = options;
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined' || isStaticContext()) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { rootMargin, threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once, rootMargin]);

  return { ref, inView };
}

/**
 * Animate an integer from 0 → target once `active` becomes true. Renders the
 * final value immediately in any static context so the number is always correct
 * for crawlers and reduced-motion users.
 */
export function useCountUp(target: number, active: boolean, durationMs = 1400): number {
  const [value, setValue] = useState<number>(() => (isStaticContext() ? target : 0));
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;

    if (isStaticContext() || typeof requestAnimationFrame === 'undefined') {
      setValue(target);
      return;
    }

    let raf = 0;
    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, durationMs]);

  return value;
}

/**
 * Subtle mouse-parallax tilt for the hero device cluster. Tracks pointer motion
 * across the nearest positioned ancestor (a bigger, calmer tracking zone) and
 * writes `--rx` / `--ry` CSS variables the element consumes in its transform.
 * Fine-pointer only (skips touch), and fully disabled under reduced motion.
 */
export function useTilt<T extends HTMLElement = HTMLDivElement>(maxDeg = 6) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || isStaticContext()) return;
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      !window.matchMedia('(pointer: fine)').matches
    ) {
      return;
    }

    const zone = el.parentElement ?? el;
    let raf = 0;

    const onMove = (e: PointerEvent) => {
      const rect = zone.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--ry', `${(px * maxDeg).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${(-py * maxDeg).toFixed(2)}deg`);
      });
    };
    const reset = () => {
      cancelAnimationFrame(raf);
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    };

    zone.addEventListener('pointermove', onMove);
    zone.addEventListener('pointerleave', reset);
    return () => {
      cancelAnimationFrame(raf);
      zone.removeEventListener('pointermove', onMove);
      zone.removeEventListener('pointerleave', reset);
    };
  }, [maxDeg]);

  return ref;
}
